"use client";

import { TarotRequestCreate, TarotRequestResponse, Language } from "@/types/tarot";
import { PricingConfig } from "@/services/pricing";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { TarotApiService } from "@/services/tarotApi";
import MercadoPagoPaymentBrick from "@/components/payments/MercadoPagoPaymentBrick";
import { config, getApiLanguage } from "@/config/app";
import { useI18n } from "@/i18n";
import { formatCurrencyFromCents } from "@/config/currency";
import { getCampaignSource } from "@/lib/campaignAttribution";
import { getPersistedUtm } from "@/lib/tracking";
import { getFlowId } from "@/services/analytics";
import { saveHotmartReturnContext } from "@/lib/hotmartReturn";
import Image from "next/image";

const StripePaymentBrick = lazy(() => import("@/components/payments/StripePaymentBrick"));

interface Step8PaymentProps {
  readonly formData: TarotRequestCreate;
  readonly onPaymentError?: (error: string) => void;
  readonly onMercadoPagoSuccess?: (status: "approved" | "authorized") => void;
  readonly pricingConfig: PricingConfig;
  readonly onRequestIdReady?: (id: number) => void;
  readonly onRequestCreated?: (request: TarotRequestResponse) => void;
}

export function Step8Payment({
  formData,
  onPaymentError = (error) => console.error("Erro no pagamento:", error),
  onMercadoPagoSuccess,
  pricingConfig,
  onRequestIdReady,
  onRequestCreated,
}: Step8PaymentProps) {
  const { t } = useI18n();
  const formatBirthDate = (value?: string | null) => {
    const base = (value || "").trim();
    if (!base) return t("value.not_informed", "Não informado");

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(base);
    if (!match) return base;

    const [, year, month, day] = match;
    return config.i18n.language === "en-US"
      ? `${month}/${day}/${year}`
      : `${day}/${month}/${year}`;
  };

  const [mpLoading, setMpLoading] = useState(false);
  const [mpRequestId, setMpRequestId] = useState<number | null>(null);
  const [couponInput, setCouponInput] = useState<string>("");
  const [couponApplied, setCouponApplied] = useState<boolean>(false);
  const [showCouponField, setShowCouponField] = useState<boolean>(false);
  const [priorityDelivery, setPriorityDelivery] = useState(false);
  const [paymentAmountCents, setPaymentAmountCents] = useState<number | null>(null);
  const [previousPayerEmailResult, setPreviousPayerEmailResult] = useState<{
    phone: string;
    email: string | null;
  } | null>(null);
  const lookupPreviousPayerEmail =
    config.payments.provider === "mercadopago" &&
    formData.delivery_option === "whatsapp" &&
    Boolean(formData.querent_phone?.trim());
  const payerEmailLookupLoading = lookupPreviousPayerEmail &&
    previousPayerEmailResult?.phone !== formData.querent_phone;

  useEffect(() => {
    const phone = formData.querent_phone;
    if (!lookupPreviousPayerEmail || !phone) return;

    let cancelled = false;
    TarotApiService.getPreviousPayerEmail(phone)
      .then((email) => {
        if (!cancelled) setPreviousPayerEmailResult({ phone, email });
      })
      .catch(() => {
        if (!cancelled) setPreviousPayerEmailResult({ phone, email: null });
      });
    return () => { cancelled = true; };
  }, [lookupPreviousPayerEmail, formData.querent_phone]);

  const payerEmail = formData.delivery_option === "whatsapp"
    ? (previousPayerEmailResult?.phone === formData.querent_phone
      ? previousPayerEmailResult.email || undefined
      : undefined)
    : formData.querent_email;

  const couponConfigured =
    (config.pricing.coupon?.name || "").trim().length > 0 &&
    (config.pricing.coupon?.percent || 0) > 0;

  const normalizedCouponName = (config.pricing.coupon?.name || "")
    .trim()
    .toLowerCase();

  const couponValid = useMemo(() => {
    if (!couponConfigured) return false;
    return couponInput.trim().toLowerCase() === normalizedCouponName;
  }, [couponConfigured, couponInput, normalizedCouponName]);

  const basePriceCents = pricingConfig.price_cents || 0;
  const discountedPriceCents = useMemo(() => {
    if (!couponApplied || !couponValid) return basePriceCents;
    const pct = Math.max(
      0,
      Math.min(100, Number(config.pricing.coupon?.percent || 0)),
    );
    const discount = Math.round((basePriceCents * pct) / 100);
    return Math.max(0, basePriceCents - discount);
  }, [basePriceCents, couponApplied, couponValid]);

  const discountAmountCents = useMemo(() => {
    if (!(couponApplied && couponValid)) return 0;
    return Math.max(0, basePriceCents - discountedPriceCents);
  }, [couponApplied, couponValid, basePriceCents, discountedPriceCents]);

  const marketingOriginalPriceCents = useMemo(() => {
    const rawOriginalCents = Math.round(basePriceCents / 0.7);
    const whole = Math.floor(rawOriginalCents / 100);
    const lower90 = whole * 100 + 90;
    const upper90 = (whole + 1) * 100 + 90;
    return (
      Math.abs(rawOriginalCents - lower90) <= Math.abs(upper90 - rawOriginalCents)
        ? lower90
        : upper90
    );
  }, [basePriceCents]);

  const payablePriceCents =
    couponApplied && couponValid ? discountedPriceCents : basePriceCents;
  const priorityConfig = pricingConfig.priority_delivery;
  const priorityFeeCents =
    priorityDelivery && priorityConfig?.enabled ? priorityConfig.fee_cents : 0;
  const totalPriceCents = payablePriceCents + priorityFeeCents;
  const showCouponOption = couponConfigured && !mpLoading && !mpRequestId;

  const fromOriginalToCurrentLabel = t(
    "step8.product.from_to",
    "De {price} por",
  ).replace("{price}", formatCurrencyFromCents(marketingOriginalPriceCents));

  const formatDeliveryTime = (minutes: number) => {
    if (minutes >= 60 && minutes % 60 === 0) {
      return t("step8.delivery.hours", "{hours} horas").replace(
        "{hours}",
        String(minutes / 60),
      );
    }
    return t("step8.delivery.minutes", "{minutes} minutos").replace(
      "{minutes}",
      String(minutes),
    );
  };

  const standardDeliveryTime = formatDeliveryTime(
    pricingConfig.standard_delivery_minutes ?? 120,
  );
  const priorityDeliveryTime = priorityConfig
    ? formatDeliveryTime(priorityConfig.delivery_minutes)
    : "";

  const startPayment = async () => {
    try {
      setMpLoading(true);
      try {
        sessionStorage.removeItem("mp_payer_email");
      } catch {
        // Storage can be unavailable in private browsing.
      }
      await TarotApiService.forceTokenRenewal();

      const deliveryOption = formData.delivery_option || "email";
      const wantsWhatsapp = deliveryOption === "whatsapp" || deliveryOption === "email_whatsapp";
      const wantsEmail = deliveryOption === "email" || deliveryOption === "email_whatsapp";

      const created = await TarotApiService.createTarotRequestAwaitingPayment({
        ...formData,
        campaign_source: getCampaignSource(),
        ...getPersistedUtm(),
        flow_id: getFlowId(),
        language: getApiLanguage() as Language,
        payment_provider: config.payments.provider,
        coupon_code: couponApplied && couponValid ? couponInput.trim() : undefined,
        priority_delivery: priorityDelivery,
        relationship_status: {
          together: false,
          separated: false,
          romantic_interest: true,
        },
        delivery_option: deliveryOption,
        querent_email: wantsEmail ? formData.querent_email : "",
        querent_phone: wantsWhatsapp
          ? (() => {
              const raw = formData.querent_phone || "";
              const onlyDigits = raw.replaceAll(/\D/g, "");
              const cc = formData.querent_phone_country || "BR";
              const ddiMap: Record<string, string> = {
                BR: "55",
                US: "1",
                AR: "54",
                PT: "351",
                ES: "34",
                MX: "52",
                CO: "57",
                CL: "56",
              };
              if (raw.trim().startsWith("+")) return "+" + onlyDigits;
              const ddi = ddiMap[cc] || "55";
              return `+${ddi}${onlyDigits}`;
            })()
          : "",
        querent_phone_confirmation: wantsWhatsapp
          ? (() => {
              const raw =
                formData.querent_phone_confirmation || formData.querent_phone || "";
              if (!raw) return raw;
              const onlyDigits = raw.replaceAll(/\D/g, "");
              const cc = formData.querent_phone_country || "BR";
              const ddiMap: Record<string, string> = {
                BR: "55",
                US: "1",
                AR: "54",
                PT: "351",
                ES: "34",
                MX: "52",
                CO: "57",
                CL: "56",
              };
              if (raw.trim().startsWith("+")) return "+" + onlyDigits;
              const ddi = ddiMap[cc] || "55";
              return `+${ddi}${onlyDigits}`;
            })()
          : "",
      });

      if (onRequestIdReady && typeof created.id === "number") {
        onRequestIdReady(created.id);
      }
      onRequestCreated?.(created);

      if (config.payments.provider === "hotmart") {
        const checkout = await TarotApiService.createHotmartCheckout(created.id);
        saveHotmartReturnContext({
          requestId: created.id,
          checkoutToken: checkout.checkout_token,
          querentName: formData.querent.full_name || "Cliente",
          email: wantsEmail ? formData.querent_email || undefined : undefined,
          deliveryOption,
          whatsappNumber:
            formData.delivery_option !== "email"
              ? formData.querent_phone || undefined
              : undefined,
          amountCents: created.hotmart_amount_cents ?? totalPriceCents,
          currency: created.hotmart_currency || pricingConfig.currency,
          priorityDelivery: created.is_priority_delivery ?? priorityDelivery,
          priorityDeliveryFeeCents:
            created.priority_delivery_fee_cents ?? priorityFeeCents,
          deliveryTargetMinutes:
            created.delivery_target_minutes ??
            (priorityDelivery
              ? priorityConfig?.delivery_minutes
              : pricingConfig.standard_delivery_minutes),
          createdAt: Date.now(),
        });
        window.location.assign(checkout.checkout_url);
        return;
      }

      setMpRequestId(created.id as unknown as number);
      setPaymentAmountCents(
        created.stripe_amount_cents ?? created.mp_amount_cents ?? totalPriceCents,
      );
    } catch (e) {
      const msg =
        (e as Error)?.message || "Falha ao iniciar pagamento Mercado Pago";
      onPaymentError?.(msg);
    } finally {
      setMpLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      {config.ui.showPaymentRequestSummary && (
        <div className="bg-white/40 p-6 rounded-lg border border-[rgba(232,115,115,0.2)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-tarot-marrom-escuro mb-2 font-title">
                {t("summary.querent", "👤 Informações do Consulente")}
              </h4>
              <ul className="form-description-text space-y-2">
                <li>
                  <span className="font-title">{t("summary.name", "Nome")}:</span>{" "}
                  {formData.querent.full_name}
                </li>
                {formData.querent_phone?.trim() && (
                  <li>
                    <span className="font-title">
                      {t("summary.whatsapp", "WhatsApp")}:
                    </span>{" "}
                    {formData.querent_phone}
                  </li>
                )}
                {formData.delivery_option !== "whatsapp" && formData.querent_email?.trim() && (
                  <li>
                    <span className="font-title">
                      {t("summary.email", "Email")}:
                    </span>{" "}
                    {formData.querent_email}
                  </li>
                )}
                <li>
                  <span className="font-title">
                    {t("summary.birth_date", "Data de nascimento")}:
                  </span>{" "}
                  {formatBirthDate(
                    (
                      formData.querent as unknown as {
                        birth_date?: string | null;
                      }
                    ).birth_date,
                  )}
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-tarot-marrom-escuro mb-2 font-title">
                {t("summary.channeled", "💫 Pessoa Canalizada")}
              </h4>
              <ul className="form-description-text space-y-2">
                <li>
                  <span className="font-title">{t("summary.name", "Nome")}:</span>{" "}
                  {formData.channeled_person.full_name}
                </li>
              </ul>
            </div>
          </div>

          {formData.desired_answers && (
            <div className="mt-6 pt-4 border-t border-[rgba(232,115,115,0.15)]">
              <h4 className="text-tarot-marrom-escuro mb-2 font-title">
                {t("step8.happening.title", "💌 O que está acontecendo")}
              </h4>
              <p className="form-description-text bg-white p-4 rounded-lg border border-[rgba(232,115,115,0.15)]">
                {formData.desired_answers}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="bg-white p-6 rounded-lg border-2 border-[rgba(232,115,115,0.3)] shadow-lg">
        <p className="form-description-text mb-4 whitespace-pre-line">
          {t(
            "step8.value_reinforcement",
            "✨ Sua carta está pronta para ser escrita\nAssim que você confirmar, sua leitura será iniciada com base na sua situação.",
          )}
        </p>

        <div className="bg-white/50 rounded-lg p-5 border border-[rgba(232,115,115,0.2)] mb-4 text-center">
          <div className="flex flex-col items-center justify-center gap-1">
            <p className="text-base text-tarot-marrom-escuro font-title">
              {t("step8.product.name", "Carta Canalizada")}
            </p>
            <div className="text-center">
              <p className="text-sm text-gray-500 font-subtitle">
                {fromOriginalToCurrentLabel}
              </p>
              {couponApplied && couponValid && (
                <p className="text-xs text-emerald-700 font-subtitle">
                  - {formatCurrencyFromCents(discountAmountCents)}
                </p>
              )}
              <p className="text-2xl text-emerald-700 font-title leading-tight">
                {formatCurrencyFromCents(payablePriceCents)}
              </p>
              {mpRequestId && priorityDelivery && priorityConfig?.enabled && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-left">
                  <p className="font-title text-tarot-marrom-escuro">
                    {t("step8.priority.selected_title", "⚡ Entrega prioritária selecionada")}
                  </p>
                  <p className="mt-1 text-sm text-tarot-marrom">
                    {t(
                      "step8.priority.selected_fee",
                      "Entrega prioritária: + {price}",
                    ).replace(
                      "{price}",
                      formatCurrencyFromCents(priorityConfig.fee_cents),
                    )}
                  </p>
                  <p className="mt-1 text-sm text-tarot-marrom">
                    {t(
                      "step8.priority.selected_time",
                      "Entrega em até {time}, após a confirmação do pagamento.",
                    ).replace("{time}", priorityDeliveryTime)}
                  </p>
                  <p className="mt-2 font-title text-emerald-800">
                    {t(
                      "step8.priority.total",
                      "Total com entrega prioritária: {price}",
                    ).replace(
                      "{price}",
                      formatCurrencyFromCents(paymentAmountCents ?? totalPriceCents),
                    )}
                  </p>
                </div>
              )}
              <p className="text-xs text-tarot-marrom font-subtitle mt-0.5">
                {t("step8.product.one_time", "Pagamento único")}
              </p>
            </div>
          </div>
        </div>

        {priorityConfig?.enabled && !mpRequestId && (
          <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-lg border-2 border-amber-300 bg-amber-50/70 p-4 transition hover:border-amber-400">
            <input
              type="checkbox"
              checked={priorityDelivery}
              onChange={(event) => setPriorityDelivery(event.target.checked)}
              disabled={mpLoading}
              className="mt-1 h-5 w-5 accent-emerald-600"
            />
            <span className="flex-1">
              <span className="flex flex-wrap items-center justify-between gap-2 font-title text-tarot-marrom-escuro">
                <span>{t("step8.priority.title", "⚡ Entrega Prioritária")}</span>
                <span>+ {formatCurrencyFromCents(priorityConfig.fee_cents)}</span>
              </span>
              <span className="mt-1 block text-sm text-tarot-marrom">
                {t(
                  "step8.priority.description",
                  "Antecipe sua entrega de até {standard} para até {priority}.",
                )
                  .replace("{standard}", standardDeliveryTime)
                  .replace("{priority}", priorityDeliveryTime)}
              </span>
              <span className="mt-1 block text-xs italic text-tarot-marrom/80">
                {t(
                  "step8.priority.after_payment",
                  "Prazo contado após a confirmação do pagamento.",
                )}
              </span>
            </span>
          </label>
        )}

        {priorityDelivery && priorityConfig?.enabled && !mpRequestId && (
          <p className="mb-4 text-right font-title text-emerald-800">
            {t(
              "step8.priority.total",
              "Total com entrega prioritária: {price}",
            ).replace("{price}", formatCurrencyFromCents(totalPriceCents))}
          </p>
        )}

        {showCouponOption && (
          <div className="mb-2">
            {!showCouponField ? (
              <button
                type="button"
                onClick={() => setShowCouponField(true)}
                className="inline-flex items-center gap-2 text-sm text-tarot-marrom/80 underline underline-offset-2 hover:text-tarot-marrom"
              >
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="h-5 w-5 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.2 13.6L13.6 20.2a2 2 0 0 1-2.8 0L3.8 13.2a2 2 0 0 1 0-2.8L10.4 3.8a2 2 0 0 1 1.4-.6H18a2 2 0 0 1 2 2v6.2a2 2 0 0 1-.6 1.4Z" />
                  <circle cx="15.5" cy="8.5" r="1.2" fill="currentColor" stroke="none" />
                </svg>
                {t("step8.coupon.link", "Tenho um cupom de Desconto")}
              </button>
            ) : (
              <div className="bg-white rounded-lg p-3 border border-[rgba(232,115,115,0.2)]">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <input
                    type="text"
                    id="coupon-input"
                    value={couponInput}
                    onChange={(e) => {
                      setCouponInput(e.target.value);
                      setCouponApplied(false);
                    }}
                    placeholder={t("step8.coupon.placeholder", "Insira seu cupom")}
                    className="flex-1 border rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed"
                    disabled={couponApplied && couponValid}
                  />
                  {couponApplied ? (
                    <button
                      type="button"
                      onClick={() => {
                        setCouponApplied(false);
                        setCouponInput("");
                      }}
                      className="px-4 py-2 rounded bg-gray-200 text-gray-800 text-sm hover:bg-gray-300"
                    >
                      {t("step8.coupon.remove", "Remover")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setCouponApplied(true)}
                      className="w-full sm:w-auto px-4 py-2 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-60"
                      disabled={couponInput.trim().length === 0}
                      title={
                        couponInput.trim().length === 0
                          ? t("step8.coupon.enter_coupon_title", "Digite um cupom")
                          : t("step8.coupon.apply_title", "Aplicar")
                      }
                    >
                      {t("step8.coupon.apply", "Aplicar")}
                    </button>
                  )}
                </div>
                {couponApplied && !couponValid && (
                  <p className="text-sm text-red-600 mt-1">
                    {t("step8.coupon.invalid", "Cupom inválido.")}
                  </p>
                )}
                {couponApplied && couponValid && (
                  <p className="text-sm text-emerald-700 mt-1">
                    {t("step8.coupon.applied_prefix", "Cupom aplicado:")}{" "}
                    {config.pricing.coupon?.percent}% OFF
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="space-y-4">
          {mpRequestId ? (
            <div className="pt-2 relative min-h-[400px]">
              {mpLoading && (
                <div
                  className="absolute inset-0 bg-gradient-to-br from-tarot-bege/95 to-white/95 backdrop-blur-sm rounded-lg z-10 flex flex-col items-center justify-center"
                  style={{ boxShadow: "inset 0 0 40px rgba(212, 175, 55, 0.2)" }}
                >
                  <div className="flex flex-col items-center gap-4 p-8">
                    <div className="relative w-20 h-20">
                      <div
                        className="absolute inset-0 rounded-full animate-spin"
                        style={{
                          background:
                            "linear-gradient(135deg, #d4af37, #f4e4b6, #d4af37)",
                          animation: "spin 1.5s linear infinite",
                        }}
                      />
                      <div className="absolute inset-2 rounded-full bg-gradient-to-br from-tarot-bege to-white" />
                      <div className="absolute inset-0 flex items-center justify-center text-3xl">
                        💳
                      </div>
                    </div>
                    <div className="text-center space-y-2">
                      <p className="text-lg font-title text-tarot-marrom-escuro animate-pulse">
                        {config.payments.provider === "stripe"
                          ? t("step8.loading.connecting_stripe", "Conectando com Stripe...")
                          : config.payments.provider === "hotmart"
                            ? t("step8.loading.connecting_hotmart", "Conectando com Hotmart...")
                            : t("step8.loading.connecting", "Conectando com Mercado Pago...")}
                      </p>
                      <p className="text-sm font-subtitle text-tarot-marrom/80">
                        {t(
                          "step8.loading.wait",
                          "Aguarde enquanto preparamos seu pagamento seguro...",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {config.payments.provider === "mercadopago" && formData.delivery_option === "whatsapp" && !payerEmail && (
                <p className="mb-3 text-sm text-tarot-marrom">
                  {t("step8.payer_email_note", "O Mercado Pago solicitará o email do pagador para processar o pagamento. A carta será entregue apenas pelo WhatsApp.")}
                </p>
              )}
              {config.payments.provider === "stripe" ? (
                <Suspense fallback={<p className="text-center text-sm text-tarot-marrom">{t("step8.loading.connecting_stripe", "Conectando com Stripe...")}</p>}>
                  <StripePaymentBrick
                    requestId={mpRequestId}
                    onSuccess={onMercadoPagoSuccess}
                    payerEmail={payerEmail}
                  />
                </Suspense>
              ) : (
                <MercadoPagoPaymentBrick
                  requestId={mpRequestId}
                  onSuccess={onMercadoPagoSuccess}
                  amountOverrideCents={paymentAmountCents ?? undefined}
                  payerEmail={payerEmail}
                />
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <button
                onClick={startPayment}
                disabled={mpLoading || payerEmailLookupLoading}
                className="group relative px-8 py-4 text-lg font-bold text-white rounded-xl transition-all duration-300 shadow-lg font-title disabled:opacity-60"
                style={{
                  background:
                    "linear-gradient(to bottom, #059669, #047857, #065f46)",
                  border: "2px solid #34d399",
                  boxShadow: "0 6px 14px rgba(16, 185, 129, 0.24)",
                }}
              >
                <span
                  className="absolute inset-0 rounded-xl blur-md transition-all duration-300"
                  style={{ backgroundColor: "rgba(52, 211, 153, 0.14)" }}
                  aria-hidden
                />
                <span className="relative flex items-center justify-center gap-2">
                  {(mpLoading || payerEmailLookupLoading) && <span className="inline-block animate-spin">⏳</span>}
                  {mpLoading || payerEmailLookupLoading
                    ? t("step8.pay.start_loading", "Iniciando…")
                    : t("step8.pay.start", "✨ Receber minha carta agora")}
                </span>
              </button>
              <p className="text-sm text-tarot-marrom mt-2">
                {t("step8.pay.methods", "🔒 Pagamento seguro via Pix ou Cartão")}
              </p>
              <p className="text-sm text-tarot-marrom mt-1">
                {priorityDelivery && priorityConfig
                  ? t(
                      "step8.priority.badge",
                      "Entrega prioritária em até {time}",
                    ).replace("{time}", priorityDeliveryTime)
                  : t("step8.secure.badge", "⏱ Entrega em até {time}").replace(
                      "{time}",
                      standardDeliveryTime,
                    )}
              </p>
              {config.payments.provider === "stripe" &&
                config.i18n.language === "en-US" && (
                <div className="mt-3 flex justify-center">
                  <Image
                    src="/images/stripe-logo.webp"
                    alt="Stripe"
                    width={96}
                    height={40}
                    className="h-8 w-auto"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
